import {
  useState,
  useImperativeHandle,
  forwardRef,
  useContext,
  useEffect,
  useRef
} from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import { Tooltip } from "@mui/material";
import Delete from "../../assets/img/icons/delete-row.png";
import Info from "../../assets/img/icons/info.png";
import { showNotificationToast } from "../../helpers/Messages";
import AuthContext from "../auth/AuthContext";
import { commonOutlinedInputStyles } from "../../helpers/InfoTableHelper";
import TableValueInput from "./TableValueInput";

const InfoTable = forwardRef(
  ({
    rows,
    setRows,
    selectedOption,
    setLoading,
    tableHeight,
    statusButtons,
    inspectionType,
    trackingId,
    changesMade,
    setChangesMade
  },ref) => {

  const [editingRowIndex, setEditingRowIndex] = useState(null);

  const [removingRows, setRemovingRows] = useState({});
  const [newRowKeys, setNewRowKeys] = useState({});

  const authContext = useContext(AuthContext);
  const rowRefs = useRef({});

  useEffect(() => {
    Object.keys(newRowKeys).forEach(key => {
      // small delay to allow element to be rendered with opacity: 0 first
      setTimeout(() => {
        if (rowRefs.current[key]) {
          rowRefs.current[key].classList.add('show');
        }
      }, 50);
    });
  }, [rows]);

  useImperativeHandle(ref, () => ({
    addRow(newRowData) {
      const rowKey = newRowData.key || `new-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
      const rowWithKey = { ...newRowData, key: rowKey };

      setRows((prevRows) => [rowWithKey, ...prevRows]);
      setEditingRowIndex(0);
      setChangesMade(true);
      setNewRowKeys((prev) => ({ ...prev, [rowKey]: true })); // mark this row for slide-in
    },
  }));

  const handleEditRow = (index) => {
    if (authContext.permissions.includes("can_edit_info") && editingRowIndex !== index) {
      setEditingRowIndex(index);
      setChangesMade(true);
    }
  };

  const handleLabelChange = (e, index) => {
    const updatedRows = [...rows];
    const newLabel = e.target.value;
    updatedRows[index].label = newLabel;
    setRows(updatedRows);
    setChangesMade(true);
  };

  const handleValueChange = (e, index, type) => {
    const updatedRows = [...rows];
    const row = updatedRows[index];

    let newValue;
    switch (type) {
      case "boolean":
        newValue = e.target.checked;
        break;
      case "multi":
        newValue = e.target.value;
        break;
      case "int":
        const rawValue = e.target.value;
        // if the input is completely empty, allow it to be empty in state
        if (rawValue === "") {
          newValue = "";
        } else {
          newValue = parseInt(rawValue, 10);
          if (isNaN(newValue)) {
            newValue = "";
          } else if (newValue < 0) {
            newValue = 0;
          }
        }
        break;
      case "choice":
        newValue = e.target.value === "----" ? null : e.target.value;
        break;
      default:
        newValue = e.target.value;
    }

    row.information.value = newValue;
    setRows(updatedRows);
    setChangesMade(true);
  };

  const handleDeleteRow = (indexToDelete) => {
    const rowKeyToDelete = rows[indexToDelete].key;

    setRemovingRows((prev) => ({ ...prev, [rowKeyToDelete]: true }));

    setTimeout(() => {
      setRows((prevRows) => prevRows.filter((_, i) => i !== indexToDelete));
      setEditingRowIndex(null);
      setChangesMade(true);
      // cleans up the removing state after removal
      setRemovingRows((prev) => {
        const newState = { ...prev };
        delete newState[rowKeyToDelete];
        return newState;
      });
    }, 250);
  };

 const updateTable = async () => {
    const requestType = inspectionType === 'distribution' ? 2 : 1;
    setLoading(true);
    try {
      const dataPayload = {};

      rows.forEach(row => {
        let fieldName;
        if (row.information.isCustom) {
          // for custom rows, derive the fieldName from the label
          fieldName = row.label
        } else {
          // for existing (schema-defined) rows, use the original key
          fieldName = row.key;
        }

        if (!fieldName) {
          showNotificationToast("Skipped row with empty label. Please provide a label for all new fields.");
          return;
        }

        dataPayload[fieldName] = row.information.value;
      });

      const payload = {
        template_id: selectedOption,
        data: dataPayload,
      };

      const updated_template_data = await window.apiHelper.updateStructureInfoData(
        trackingId,
        requestType,
        payload
      );

      if (updated_template_data) {
        showNotificationToast("Updated Successfully!");
        setChangesMade(false);
      }
    } catch (error) {
      console.error("Error updating data:", error);
      showNotificationToast("Error updating information.");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveChanges = () => {
    updateTable(rows);
    setEditingRowIndex(null); // exit editing mode for all rows after saving
  };


  return (
    <div>
      <Paper sx={{ height: tableHeight }}>
        <TableContainer sx={{ height: "100%" }}>
          <Table size="small" aria-label="info table" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell
                  align="center"
                  sx={{
                    backgroundColor: "#eef1f7",
                    width: "45%",
                    fontWeight: "bold",
                    fontFamily: "inherit",
                  }}
                >
                  Label
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    backgroundColor: "#eef1f7",
                    width: "40%",
                    fontWeight: "bold",
                    fontFamily: "inherit",
                  }}
                >
                  Value
                </TableCell>
                {authContext.permissions.includes("can_edit_info") && (
                  <TableCell
                    align="center"
                    sx={{
                      backgroundColor: "#eef1f7",
                      width: "15%",
                      fontWeight: "bold",
                      fontFamily: "inherit",
                    }}
                  >
                    Actions
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => {
                const label = row.label;
                const isCustom = row.information.isCustom;
                const isEditing = index === editingRowIndex;
                const rowKey = row.key || index;

                const rowClassName = `table-row-slide ${newRowKeys[rowKey] ? '' : 'show'} ${removingRows[rowKey] ? 'removing' : ''}`;

                return (
                  <TableRow
                    key={rowKey}
                    ref={(el) => (rowRefs.current[rowKey] = el)}
                    className={rowClassName}
                  >
                    {isEditing ? (
                      <>
                        <TableCell
                          align="center"
                          sx={{ width: "45%", fontFamily: "inherit" }}
                        >
                          {isCustom ? (
                            <TextField
                              size="small"
                              variant="outlined"
                              placeholder="New Field"
                              value={row.label}
                              onChange={(e) => handleLabelChange(e, index)}
                              sx={commonOutlinedInputStyles}
                              multiline
                               InputProps={{
                                sx: {
                                  fontFamily: 'inherit'
                                }
                              }}
                            />
                          ) : (
                            <span>{label}</span>
                          )}
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{ width: "40%", fontFamily: "inherit" }}
                        >
                          {/* custom component to handle input types */}
                          <TableValueInput
                              row={row}
                              index={index}
                              isEditing={true}
                              handleValueChange={handleValueChange}
                          />
                        </TableCell>
                        {authContext.permissions.includes("can_edit_info") && (
                          <TableCell align="center" sx={{ width: "15%" }}>
                            <Tooltip title="Delete" arrow placement="top">
                              <span>
                                <button
                                  onClick={() => handleDeleteRow(index)}
                                  className="save-btn"
                                >
                                  <img
                                    src={Delete}
                                    alt="delete"
                                    style={{ width: "17.5px" }}
                                  />
                                </button>
                              </span>
                            </Tooltip>
                          </TableCell>
                        )}
                      </>
                    ) : (
                      <>
                        <TableCell
                          align="center"
                          sx={{
                            width: "45%",
                            fontFamily: "inherit",
                            cursor: authContext.permissions.includes(
                              "can_edit_info"
                            )
                              ? "pointer"
                              : "default",
                          }}
                          onClick={() => handleEditRow(index)}
                        >
                          {label}
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            width: "40%",
                            fontFamily: "inherit",
                            cursor: authContext.permissions.includes(
                              "can_edit_info"
                            )
                              ? "pointer"
                              : "default",
                          }}
                          onClick={() => handleEditRow(index)}
                        >
                          {/* custom component to handle input types */}
                          <TableValueInput
                              row={row}
                              index={index}
                              isEditing={false}
                              handleValueChange={handleValueChange}
                          />
                        </TableCell>
                        {authContext.permissions.includes("can_edit_info") && (
                          <TableCell align="center" sx={{ width: "15%" }}>
                            <Tooltip title="Delete" arrow placement="top">
                              <span>
                                <button
                                  onClick={() => handleDeleteRow(index)}
                                  className="save-btn"
                                >
                                  <img
                                    src={Delete}
                                    alt="delete"
                                    style={{ width: "17.5px" }}
                                  />
                                </button>
                              </span>
                            </Tooltip>
                          </TableCell>
                        )}
                      </>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      <div
        className={`info-footer ${
          rows.length > 0 || changesMade
            ? "justify-between"
            : "justify-center"
        }`}
      >
        {rows.length > 0 && (
          <Tooltip
            title="Yellow colour symbolizes information is assumed due to insufficient evidence"
            arrow
            placement="top"
          >
            <span>
              <button className="info-btn">
                <img src={Info} alt="info icon" style={{ width: "25px" }} />
              </button>
            </span>
          </Tooltip>
        )}
        {statusButtons}
        {changesMade && (
          <button
            className="reset-button"
            onClick={handleSaveChanges}
            style={{ marginBottom: 0 }}
          >
            Save Changes
          </button>
        )}
      </div>
    </div>
  );
});

export default InfoTable;